import { gql, useQuery } from '@apollo/client';
import { GraphQLSchema, isCompositeType } from 'graphql';
import _ from 'lodash';
import * as monacoEditor from 'monaco-editor';
import React from 'react';

import { FieldTimingsPercentile } from 'src/app/graph/explorerPage/explorerSettings/useExplorerSettings';
import {
  getAncestorStates,
  runOnlineParser,
} from 'src/app/graph/explorerPage/helpers/graphqlParseHelpers';
import {
  operationPathToGraphQLPath,
  parsePathToOperationPath,
} from 'src/app/graph/explorerPage/helpers/operationPathHelpers';
import { useActiveDefinitionDetails } from 'src/app/graph/explorerPage/hooks/useActiveDefinitionDetails';
import { GraphRef } from 'src/app/graph/hooks/useGraphRef';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { format } from 'src/lib/format';
import { unwrapType } from 'src/lib/graphql/graphqlHelpers';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { EditorHint } from '../editorHint/EditorHint';

interface Field {
  fieldName: string;
  parentType: string;
}

interface FieldData {
  field: Field;
  lineNumbers: number[];
}

function keyForField(field: Field): string {
  // We assume that these values are valid GraphQL identifiers which do
  // not contain dots.
  return `${field.parentType}.${field.fieldName}`;
}

type OperationFieldData = Record<string, FieldData>;
function getFieldsInOperation(
  operation: string,
  schema: GraphQLSchema,
): OperationFieldData {
  const fields: OperationFieldData = {};
  runOnlineParser(operation, (state, range) => {
    switch (state.kind) {
      case 'AliasedField':
      case 'Field': {
        const editorNodes = parsePathToOperationPath(getAncestorStates(state));
        const graphQLNodes = operationPathToGraphQLPath(editorNodes, schema);
        if (graphQLNodes && graphQLNodes.length > 1) {
          const [parent, child] = graphQLNodes.slice(-2);
          const parentType = isCompositeType(parent)
            ? parent
            : parent && 'args' in parent
              ? unwrapType(parent)
              : null;
          if (parentType && child && 'args' in child) {
            // TODO deal with unions/interfaces not getting actual data here
            const field = {
              parentType: parentType.name,
              fieldName: child.name,
            };
            const key = keyForField(field);
            (fields[key] = fields[key] || {
              field,
              lineNumbers: [],
            }).lineNumbers.push(range.startLineNumber);
          }
        }
        break;
      }
      default:
        // Ignore node
        break;
    }
  });

  return fields;
}

/**
 * Attaches a result preview besides each reachable leaf field in the document
 */
export const TimingHints = ({
  graphRef,
  editor,
  schema,
  fieldTimingsPercentile,
}: {
  graphRef: GraphRef;
  editor: monacoEditor.editor.IStandaloneCodeEditor;
  schema: GraphQLSchema;
  fieldTimingsPercentile: FieldTimingsPercentile;
}) => {
  const activeDefinition = useActiveDefinitionDetails(editor, schema);
  const activePartsOfDocument = activeDefinition?.isValid
    ? activeDefinition.activePartsOfDocument
    : undefined;
  const documentLength = activePartsOfDocument?.split('\n')?.length ?? 0;

  const fieldsToQuery: OperationFieldData = React.useMemo(
    () =>
      activePartsOfDocument
        ? getFieldsInOperation(activePartsOfDocument, schema)
        : {},
    [activePartsOfDocument, schema],
  );

  const { data } = useQuery<
    GraphQLTypes.TimingHintsQuery,
    GraphQLTypes.TimingHintsQueryVariables
  >(
    gql`
      query TimingHintsQuery(
        $graphId: ID!
        $filter: ServiceFieldLatenciesFilter
        $percentile: Float!
      ) {
        service(id: $graphId) {
          id
          statsWindow(from: "-86400", to: "-0") {
            fieldLatencies(filter: $filter) {
              metrics {
                fieldHistogram {
                  durationMs(percentile: $percentile)
                }
              }
              groupBy {
                fieldName
                parentType
              }
            }
          }
        }
      }
    `,
    {
      variables: {
        graphId: graphRef.graphId,
        filter: {
          schemaTag: graphRef.graphVariant,
          or: Object.values(fieldsToQuery).map((fieldData) => fieldData.field),
        },
        percentile: parseFloat(fieldTimingsPercentile) || 0.95,
      },
      // If we dont have stats, dont request these
      skip: !Object.keys(fieldsToQuery).length,
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    },
  );

  const hints = React.useMemo(() => {
    const fieldTimingsMs: Record<string, number | null> = {};
    data?.service?.statsWindow?.fieldLatencies.forEach((timingData) => {
      const { parentType, fieldName } = timingData.groupBy;
      if (parentType && fieldName) {
        fieldTimingsMs[keyForField({ parentType, fieldName })] =
          timingData.metrics.fieldHistogram.durationMs;
      }
    });

    const lineNumbersAndFields = Object.values(fieldsToQuery).flatMap(
      ({ field, lineNumbers }) => {
        const timeMs = fieldTimingsMs[keyForField(field)];
        return lineNumbers.map((lineNumber) => ({
          lineNumber,
          hint: timeMs ? format.ms(timeMs, 1) : '-',
        }));
      },
    );
    return Object.entries(_.groupBy(lineNumbersAndFields, 'lineNumber')).map(
      ([lineNumber, hintsArray]) => {
        return [
          lineNumber,
          hintsArray.map((hint) => {
            return hint.hint;
          }),
        ] as const;
      },
    );
  }, [data, fieldsToQuery]);
  return (
    <>
      {hints.map(([lineNumber, hint]) => (
        <EditorHint
          key={`${documentLength}-${lineNumber}-${hint}`}
          editor={editor}
          lineNumber={parseInt(lineNumber, 10)}
          hint={hint.join(', ')}
        />
      ))}
    </>
  );
};
